<template>
  <div class="dynamic-page" :class="page.url">
    <HeroSection
      :title="page.title"
      :desc="updated"
      :offset="tableOfContentExists ? 0 : 2"
      class="tiny"
    />
    <div class="site-section">
      <div class="container">
        <div class="row">
          <aside
            v-if="tableOfContentExists"
            class="push-7 offset-1 md-offset-0 col col-4 sm-4 hide-for-phone"
          >
            <div class="table-of-content col-10 md-12">
              <p class="h5">
                Table of Contents
              </p>
              <ul>
                <li
                  v-for="(heading,i) in contentList"
                  :key="i"
                  class="body-2"
                  :class="{ 'selected': currentParagraph === i + 1 }"
                >
                  <a
                    :href="`#heading${i + 1}`"
                    @click.prevent="scrollToParagraph"
                    v-html="heading.innerHTML"
                  />
                </li>
              </ul>
            </div>
          </aside>
          <div
            :class="tableOfContentExists ? 'pull-5 md-pull-4 col-7 sm-8' : 'offset-2 col-8'"
          >
            <div
              class="content tos-content col"

              v-html="page.content"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import global from '@itbpbg/lms-components/src/mixins/global';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';

export default {
  components: {
    HeroSection,
  },
  mixins: [global],
  props: { info: { type: Object, required: true } },
  data() {
    return {
      page: this.info,
      contentList: '',
      currentParagraph: 0,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    tableOfContent() {
      return this.contentList.length > 0 && this.$store.state.layout !== 'mobile';
    },
    updated() {
      return `Updated ${moment(this.page.updated).format('DD MMMM YYYY')}`;
    },
    tableOfContentExists() {
      return this.page.content.includes('table-of-content');
    },
  },
  created() {
    this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    this.page.metaFullImageUrl = `${this.$config.siteUrl}/resources/${this.page.metaImage}`;

    this.page.created = this.page.created || new Date();
    this.page.updated = this.page.updated || new Date();
  },
  mounted() {
    this.contentList = document.querySelectorAll('.content .table-of-content');
    this.contentList.forEach((h, i) => {
      // eslint-disable-next-line no-param-reassign
      h.id = `heading${i + 1}`;
    });

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));

    if (document.querySelector('.shadow .table-holder')) {
      document.querySelector('.shadow .table-holder').addEventListener('scroll', (event) => {
        const { scrollLeft, scrollWidth, offsetWidth } = event.target;
        const contentWidth = scrollWidth - offsetWidth;
        if (contentWidth <= scrollLeft + 10) {
          event.target.parentElement.classList.add('no-shadow');
        } else {
          event.target.parentElement.classList.remove('no-shadow');
        }
      });
    }
  },
  methods: {
    handleScroll() {
      // 90 + 2 - fix firefox not showing current right
      if (this.contentList) {
        const current = Array.from(this.contentList).filter((s) => s.getBoundingClientRect().top - 92 <= 0);
        this.currentParagraph = current.length;
      }
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
        this.currentParagraph = this.contentList.length;
      }
    },
    async scrollToParagraph(e) {
      const yOffset = -80;
      const section = `${e?.target?.getAttribute('href') || e}`;
      const element = document.querySelector(section);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss">
.dynamic-page {
  .tos-content {
    word-break: break-word;

    .container {
      padding: 0 var(--spacing-24) !important;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      line-height: 130%;
      margin-top: 60px;
    }

    strong {
      font-weight: 600;
    }

    p {
      margin: 0 0 36px;
      color: var(--main-dark-shade-0);
    }

    ul,
    ol {
      padding-left: var(--spacing-24);

      li {
        margin-bottom: 6px;
        list-style-position: outside;
      }
    }

    p,
    > ul,
    > ol,
    table {
      font-size: 16px;
      margin-bottom: var(--spacing-32);
      margin-top: 0;
      line-height: 24px;
    }

    h2 {
      font-size: 32px;
      margin-bottom: var(--spacing-24);
    }

    > p:last-of-type {
      margin-bottom: 0;
    }

    a {
      font-weight: 500;
    }

    .table-of-content {
      &:target {
        &:before {
          content: '';
          display: block;
          height: 155px;
          margin-top: -155px;
        }
      }

      @include bp($bp-mobile) {
        &:target {
          &:before {
            content: '';
            display: block;
            height: 65px;
            margin-top: -65px;
          }
        }
      }
    }

    .shadow {
      margin-left: -170px;

      @include bp($bp-desktop) {
        width: var(--container-width) !important;
      }

      @include bp($bp-mobile) {
        position: relative;
        margin-right: -24px;
        margin-left: 0;

        .table-holder {
          overflow-x: auto;
        }

        &:not(.no-shadow):after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          width: 60px;
          background: linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0) 100%);
        }

        table.expanded {
          margin-right: var(--spacing-24);
          width: var(--container-width) !important;
        }
      }
    }

    table {
      border-width: 0;

      tr {
        border-bottom: 1px solid var(--main-light-shade-3);

        &:first-of-type {
          background-color: var(--main-light-shade-1);
          font-weight: 600;
        }

        &:not(:first-of-type) > td {
          font-size: 14px;
        }

        td {
          border-width: 0;
          padding: var(--spacing-16);
        }
      }
    }

    table.expanded {
      margin-left: 0;
      width: 100%;
    }

    @include bp($bp-laptop) {
      h2 {
        font-size: 28px;
      }
    }

    @include bp($bp-mobile) {
      ul,
      ol {
        padding-left: var(--spacing-24);
      }

      > ul,
      > ol {
        padding-left: var(--spacing-20);
      }

      h2 {
        font-size: 28px;
      }
    }
  }

  .site-section {
    padding-top: var(--spacing-100);
    padding-bottom: var(--spacing-120);

    .table-of-content {
      position: sticky;
      top: var(--spacing-100);
      border-radius: 3px;

      ul {
        background-color: var(--main-light-shade-1);
        border-left: 4px solid var(--main-light-shade-2);
        border-radius: 3px;
        padding: var(--spacing-20) 0;
        list-style: none;
        max-height: calc(100vh - var(--spacing-160));
        overflow-y: auto;
        scrollbar-color: var(--main-light-shade-3) var(--main-light-shade-3);
        scrollbar-width: thin;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background-color: var(--main-light);
        }

        &::-webkit-scrollbar-thumb {
          background-color: var(--main-light-shade-3);
          border-radius: 4px;
        }

        li {
          position: relative;

          a {
            color: var(--main-dark-shade-3);
            display: block;
            padding: 16px 25px;
            font-weight: 400;
            width: 100%;
          }

          &.selected {
            a {
              color: var(--main-dark-shade-0);
            }

            &:before {
              background-color: var(--accent-shade-1);
              content: '';
              left: -4px;
              height: 100%;
              position: absolute;
              top: 0;
              width: 4px;
              border-radius: 10px;
            }
          }
        }
      }
    }

    @include bp($bp-mobile) {
      padding-top: var(--spacing-44);
      padding-bottom: var(--spacing-80);
    }
  }
}

.fa {
  .cookie-policy .hero-section-wrapper .container .offset-2 {
    margin-left: 0;
  }

  .dynamic-page .tos-content table tr {
    border-bottom: 1px solid var(--main-light-shade-2-200);

    &:first-of-type {
      background-color: var(--main-light-shade-2);
    }
  }
}
</style>
